import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { InstallPromptBanner } from '../components/InstallPrompt';

const BLUE       = 'var(--signal)';
const BLUE_LIGHT = 'var(--signal-soft)';
const TEXT1      = 'var(--ink)';
const TEXT2      = 'var(--slate)';
const TEXT3      = 'var(--mute)';

// The webhook may set subscription_status to either of these once payment lands.
const ACTIVE_STATUSES = ['active', 'trialing'];

function Shell({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--paper)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      boxSizing: 'border-box',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        backgroundColor: 'var(--card)',
        borderRadius: 'var(--r-card)',
        padding: 40,
        boxSizing: 'border-box',
        boxShadow: 'var(--sh-raise)',
        textAlign: 'center',
      }}>
        {children}
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <div style={{
      width: 72, height: 72, borderRadius: '50%',
      backgroundColor: BLUE_LIGHT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 24px',
    }}>
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  );
}

function ClockIcon() {
  return (
    <div style={{
      width: 72, height: 72, borderRadius: '50%',
      backgroundColor: BLUE_LIGHT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 24px',
    }}>
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15 14" />
      </svg>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%',
      border: `3px solid ${BLUE_LIGHT}`,
      borderTopColor: BLUE,
      animation: 'spin 0.7s linear infinite',
      margin: '0 auto',
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const primaryButtonStyle = {
  width: '100%',
  backgroundColor: BLUE,
  color: 'var(--on-signal)',
  border: 'none',
  borderRadius: 10,
  padding: 14,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
};

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  // session_id is present in the URL but is NOT proof of payment — the
  // webhook-updated subscription_status from /auth/me is the source of truth.
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [state, setState] = useState('loading'); // 'loading' | 'active' | 'pending'
  const [refreshing, setRefreshing] = useState(false);

  async function checkStatus() {
    try {
      const updated = await refreshUser();
      setState(ACTIVE_STATUSES.includes(updated?.subscription_status) ? 'active' : 'pending');
    } catch {
      // A transient /auth/me failure is not a payment failure — show the
      // soft pending state with a manual refresh, never a hard error.
      setState('pending');
    }
  }

  useEffect(() => {
    if (sessionId) {
      // eslint-disable-next-line no-console
      console.log('payment_success_session', sessionId);
    }
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleRefresh() {
    setRefreshing(true);
    await checkStatus();
    setRefreshing(false);
  }

  if (state === 'loading') {
    return (
      <Shell>
        <Spinner />
        <p style={{
          margin: '24px 0 0',
          fontSize: 15,
          color: TEXT2,
          lineHeight: 1.6,
        }}>
          Confirming your subscription…
        </p>
      </Shell>
    );
  }

  if (state === 'pending') {
    return (
      <Shell>
        <ClockIcon />

        <h1 style={{
          margin: '0 0 10px',
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          fontWeight: 700,
          color: TEXT1,
          lineHeight: 1.25,
        }}>
          Payment received
        </h1>

        <p style={{
          margin: '0 0 32px',
          fontSize: 14,
          color: TEXT2,
          lineHeight: 1.6,
        }}>
          We're finalizing your account. This can take a moment — tap refresh
          in a few seconds.
        </p>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{ ...primaryButtonStyle, opacity: refreshing ? 0.6 : 1, cursor: refreshing ? 'default' : 'pointer' }}
        >
          {refreshing ? 'Checking…' : 'Refresh'}
        </button>

        <button
          onClick={() => navigate('/dashboard')}
          style={{
            width: '100%',
            marginTop: 12,
            backgroundColor: 'transparent',
            color: TEXT3,
            border: 'none',
            padding: 10,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Go to dashboard
        </button>
      </Shell>
    );
  }

  // state === 'active'
  return (
    <Shell>
      <CheckIcon />

      <h1 style={{
        margin: '0 0 10px',
        fontFamily: 'var(--font-serif)',
        fontSize: 26,
        fontWeight: 700,
        color: TEXT1,
        lineHeight: 1.25,
      }}>
        You're all set!
      </h1>

      <p style={{
        margin: '0 0 32px',
        fontSize: 14,
        color: TEXT2,
        lineHeight: 1.6,
      }}>
        Your TruFlow subscription is active. Welcome aboard.
      </p>

      {/* Only in this state — 'loading' and 'pending' haven't confirmed payment
          yet, and installing shouldn't be offered before it lands. Renders
          nothing on desktop, once installed, or after a previous dismissal;
          Settings keeps a permanent way back to it either way. The button below
          never depends on this — it navigates whatever the user does here. */}
      <InstallPromptBanner />

      <button
        onClick={() => navigate('/onboarding/welcome')}
        style={primaryButtonStyle}
      >
        Continue to setup
      </button>
    </Shell>
  );
}
